import { AllowedUserTypeAuthParam, AppNames } from '@pinnakl/shared/types';
import { LogLevel, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { appApiAccess } from './app-api-access';
import { EnvNames } from './environment-names.enum';
import {
  amplitudeConfigurations,
  authenticatorAuthUrls,
  authenticatorCoreApiServerUrls,
  authenticatorDatastream1Urls,
  authenticatorFileServiceUrls,
  authenticatorFrontEndErrorServiceUrls,
  crmAuthUrls,
  crmCoreApiServerUrls,
  crmDatastream1Urls,
  crmDocumentsAuthUrls,
  crmDocumentsCoreApiServerUrls,
  crmDocumentsFileServiceUrls,
  crmFileServiceUrls,
  crmFrontEndErrorServiceUrls,
  crmGrpcApiServerUrls,
  dedicatedInvestorPortalAuthUrls,
  disabledAmplitudeConfigurations,
  emailsCollectorUrls,
  growthbookClientKeys,
  investorPortalAuthUrls,
  outlookAddinAuthUrls,
  outlookAddinCoreApiServerUrls,
  outlookAddinFileServiceUrls,
  pinnaklApiUrls,
  platformAuthUrls,
  platformCoreApiServerUrls,
  platformDatastream1Urls,
  platformFileServiceUrls,
  platformFrontEndErrorServiceUrls,
  platformGrpcApiServerUrls,
  platformMarketDataGrpcApiServerUrls,
  platformTradingServiceUrls,
  portalFileServiceUrls,
  portalHttpServerUrls,
  requestTimeoutPeriods,
  vdrFileAppHosts,
} from './environment-options';
import { PinnaklEnvironment } from './environment.model';

export function createPlatformEnvironment(envName: EnvNames): PinnaklEnvironment {
  return {
    envName,
    appName: AppNames.PLATFORM_WEB,
    fileServiceUrl: platformFileServiceUrls[envName],
    pinnaklApiUrl: pinnaklApiUrls[envName],
    grpcApiServerUrl: platformGrpcApiServerUrls[envName],
    marketDataGrpcApiServerUrl: platformMarketDataGrpcApiServerUrls[envName],
    frontEndErrorServiceUrl: platformFrontEndErrorServiceUrls[envName],
    includeTesting: false,
    production: isProductionLikeEnv(envName),
    requestTimeoutPeriod: requestTimeoutPeriods[envName],
    coreApiServerUrl: platformCoreApiServerUrls[envName],
    datastream1Url: platformDatastream1Urls[envName],
    tradingServiceUrl: platformTradingServiceUrls[envName],
    authConfig: createAuthConfig(envName, platformAuthUrls, AllowedUserTypeAuthParam.INTERNAL, AppNames.PLATFORM_WEB),
    authUrl: platformAuthUrls[envName],
    amplitudeConfiguration: amplitudeConfigurations[envName],
    growthbookClientKey: growthbookClientKeys[envName],
  };
}

export function createCrmEnvironment(envName: EnvNames): PinnaklEnvironment {
  return {
    envName,
    fileServiceUrl: crmFileServiceUrls[envName],
    frontEndErrorServiceUrl: crmFrontEndErrorServiceUrls[envName],
    production: isProductionLikeEnv(envName),
    requestTimeoutPeriod: requestTimeoutPeriods[envName],
    coreApiServerUrl: crmCoreApiServerUrls[envName],
    datastream1Url: crmDatastream1Urls[envName],
    vdrFileAppHost: vdrFileAppHosts[envName],
    grpcApiServerUrl: crmGrpcApiServerUrls[envName],
    appName: AppNames.CRM,
    authConfig: createAuthConfig(envName, crmAuthUrls, AllowedUserTypeAuthParam.INTERNAL, AppNames.CRM),
    authUrl: crmAuthUrls[envName],
    emailsCollectorUrl: emailsCollectorUrls[envName],
    amplitudeConfiguration: amplitudeConfigurations[envName],
    pinnaklApiUrl: pinnaklApiUrls[envName],
  };
}

export function createInvestorPortalEnvironment(envName: EnvNames): PinnaklEnvironment {
  return {
    envName,
    fileServiceUrl: portalFileServiceUrls[envName],
    production: isProductionLikeEnv(envName),
    httpServerUrl: portalHttpServerUrls[envName],
    requestTimeoutPeriod: requestTimeoutPeriods[envName],
    appName: AppNames.INVESTOR_PORTAL,
    vdrFileAppHost: vdrFileAppHosts[envName],
    authConfig: createAuthConfig(
      envName,
      investorPortalAuthUrls,
      AllowedUserTypeAuthParam.EXTERNAL,
      AppNames.INVESTOR_PORTAL,
    ),
    authUrl: investorPortalAuthUrls[envName],
    amplitudeConfiguration: amplitudeConfigurations[envName],
  };
}

export function createDedicatedInvestorPortalEnvironment(envName: EnvNames): PinnaklEnvironment {
  return {
    envName,
    fileServiceUrl: portalFileServiceUrls[envName],
    production: isProductionLikeEnv(envName),
    httpServerUrl: portalHttpServerUrls[envName],
    requestTimeoutPeriod: requestTimeoutPeriods[envName],
    appName: AppNames.DEDICATED_INVESTOR_PORTAL,
    vdrFileAppHost: vdrFileAppHosts[envName],
    authConfig: createAuthConfig(
      envName,
      dedicatedInvestorPortalAuthUrls,
      AllowedUserTypeAuthParam.EXTERNAL,
      AppNames.DEDICATED_INVESTOR_PORTAL,
    ),
    authUrl: dedicatedInvestorPortalAuthUrls[envName],
    amplitudeConfiguration: disabledAmplitudeConfigurations[envName],
  };
}

export function createAuthenticatorEnvironment(envName: EnvNames): PinnaklEnvironment {
  return {
    envName,
    fileServiceUrl: authenticatorFileServiceUrls[envName],
    frontEndErrorServiceUrl: authenticatorFrontEndErrorServiceUrls[envName],
    production: isProductionLikeEnv(envName),
    requestTimeoutPeriod: requestTimeoutPeriods[envName],
    coreApiServerUrl: authenticatorCoreApiServerUrls[envName],
    datastream1Url: authenticatorDatastream1Urls[envName],
    vdrFileAppHost: vdrFileAppHosts[envName],
    appName: AppNames.AUTH,
    authUrl: authenticatorAuthUrls[envName],
    amplitudeConfiguration: amplitudeConfigurations[envName],
  };
}

export function createCrmDocumentsEnvironment(envName: EnvNames): PinnaklEnvironment {
  return {
    envName,
    production: isProductionLikeEnv(envName),
    requestTimeoutPeriod: requestTimeoutPeriods[envName],
    appName: AppNames.CRM_DOCUMENTS,
    vdrFileAppHost: vdrFileAppHosts[envName],
    fileServiceUrl: crmDocumentsFileServiceUrls[envName],
    authUrl: crmDocumentsAuthUrls[envName],
    coreApiServerUrl: crmDocumentsCoreApiServerUrls[envName],
    amplitudeConfiguration: disabledAmplitudeConfigurations[envName],
  };
}

export function createOutlookAddinEnvironment(envName: EnvNames): PinnaklEnvironment {
  return {
    envName,
    appName: AppNames.CRM,
    production: envName === EnvNames.prod,
    requestTimeoutPeriod: requestTimeoutPeriods[envName],
    coreApiServerUrl: outlookAddinCoreApiServerUrls[envName],
    authApiServerUrl: outlookAddinAuthUrls[envName],
    fileServiceUrl: outlookAddinFileServiceUrls[envName],
  };
}

export const authConfigIdPrefix = (envName: EnvNames): string => `pinnakl_${envName}`;

const BASE_AUTH_SCOPES = ['openid', 'profile', 'email', 'api_core', 'offline_access'];

function createAuthConfig(
  envName: EnvNames,
  authUrls: Record<EnvNames, string>,
  allowedUserTypeAuthParam: AllowedUserTypeAuthParam,
  appName: AppNames,
): OpenIdConfiguration {
  return {
    authority: authUrls[envName],
    postLogoutRedirectUri: window.location.origin,
    redirectUrl: window.location.origin,
    clientId: 'angularclient',
    // authority host is part of the id so cached OIDC discovery data is never reused after the IdP moves
    configId: `${authConfigIdPrefix(envName)}_${new URL(authUrls[envName]).host}`,
    scope: [...BASE_AUTH_SCOPES, ...(appApiAccess[appName]?.scopes ?? [])].join(' '),
    responseType: 'code',
    silentRenew: true,
    useRefreshToken: true,
    ignoreNonceAfterRefresh: true,
    logLevel: envName === EnvNames.dev ? LogLevel.Debug : LogLevel.Error,
    historyCleanupOff: false,
    autoCleanStateAfterAuthentication: true,
    triggerAuthorizationResultEvent: false,
    startCheckSession: false,
    disableRefreshIdTokenAuthTimeValidation: true,
    renewTimeBeforeTokenExpiresInSeconds: 60 * 15,
    tokenRefreshInSeconds: 60 * 10,
    autoUserInfo: false,
    unauthorizedRoute: '/login' + window.location.search,
    customParamsAuthRequest: { aut: allowedUserTypeAuthParam },
    customParamsRefreshTokenRequest: { aut: allowedUserTypeAuthParam },
    customParamsEndSessionRequest: { aut: allowedUserTypeAuthParam },
  };
}

function isProductionLikeEnv(envName: EnvNames): boolean {
  return [EnvNames.prod, EnvNames.staging].includes(envName);
}
